import React, { useEffect, useRef, useState } from "react";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { getColor, rgbaColor } from "helpers/utils";
import { tooltipFormatter } from "helpers/echart-utils";

function FinancialGraph({ financeData, graphKey, countKey, checkboxes }) {
  const hours = Array.from({ length: 24 }, (_, index) => index);
  const days = hours.map(
    (hour) => `${hour % 12 === 0 ? 12 : hour % 12} ${hour < 12 ? "am" : "pm"}`
  );

  const extractTotalSum = (data, target, graphKey) => {
    const totalSums = {
      web_shopify: [],
      web_native: [],
      crm: [],
      app_shopflo: [],
      app_juspay: [],
    };

    for (const day in data[target]) {
      const dayData = data[target][day];
      for (const category in totalSums) {
        // const totalSum = dayData[category][graphKey];
        const totalSum = dayData[category] ? dayData[category][graphKey] || 0 : 0;
        // const totalCount = dayData[category][countKey];
        const totalCount = dayData[category] ? dayData[category][countKey] || 0 : 0;
        totalSums[category].push([totalSum, totalCount]);
      }
    }

    return totalSums;
  };

  const totalTodaySums = extractTotalSum(financeData, "today", graphKey);
  const totalYesterdaySums = extractTotalSum(
    financeData,
    "yesterday",
    graphKey
  );

  const tooltipFormatter = (params) => {
    const lines = [];
    const colorData = [
      "rgba(0, 123, 255, 0.7)",
      "rgba(0, 123, 255, 0.7)",
      "rgb(255, 204, 0)",
      "rgb(255, 193, 7)",
      "rgb(239,176,193)",
      "rgb(239,176,193)",
      "rgb(23, 162, 184)",
      "rgb(23, 162, 184)",
      "rgb(80,147,84)",
      "rgb(80,147,84)"
    ];
    let axisData = "";
    params.forEach((param, index) => {
      const { value, axisValue, seriesName, data, marker } = param;
      axisData = axisValue;
      const markerStyle = `display:inline-block;width:8px;height:8px;margin-right:8px;background-color:${colorData[index]} ;border-radius:50%;`;
      const markerElement = `<span style="${markerStyle}"></span>`;
      lines.push(
        `${markerElement}${seriesName}: ${value} (Count : ${data?.customInfo1})`
      );
    });
    lines.unshift(axisData);
    return lines.join("<br>");
  };

  const getSeries = (name, data, color, lineStyleType, areaColor, check) => {
    const series = {
      name,
      type: "line",
      data: data.map((value) => ({
        value: value[0],
        customInfo1: value[1],
      })),
      lineStyle: {
        color,
        type: lineStyleType,
      },
      itemStyle: {
        borderColor: color,
        borderWidth: 2,
        backgroundColor: "transparent",
      },
      symbol: "circle",
      symbolSize: 10,
      emphasis: {
        scale: true,
      },
      areaStyle: {
        color: {
          type: "linear",
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            {
              offset: 0,
              color: rgbaColor(areaColor, 0.2),
            },
            {
              offset: 1,
              color: rgbaColor(areaColor, 0),
            },
          ],
        },
      },
    };
    if (check) {
      return series;
    } else {
      return null;
    }
  };

  const getOption = () => {
    const series = [
      getSeries(
        "Web Shopify",
        totalTodaySums?.web_shopify,
        getColor("primary"),
        "solid",
        getColor("primary"),
        checkboxes.web_shopify
      ),
      getSeries(
        "Prev Web Shopify",
        totalYesterdaySums?.web_shopify,
        getColor("primary"),
        "dashed",
        getColor("primary"),
        checkboxes?.web_shopify
      ),
      getSeries(
        "Web Native",
        totalTodaySums?.web_native,
        rgbaColor(getColor("warning"), 0.3),
        "solid",
        getColor("warning"),
        checkboxes?.web_native
      ),
      getSeries(
        "prev Web Native",
        totalYesterdaySums?.web_native,
        rgbaColor(getColor("warning"), 0.3),
        "dashed",
        getColor("warning"),
        checkboxes?.web_native
      ),
      getSeries(
        "CRM",
        totalTodaySums?.crm,
        rgbaColor(getColor("danger"), 0.3),
        "solid",
        getColor("danger"),
        checkboxes?.crm
      ),
      getSeries(
        "prev CRM",
        totalYesterdaySums?.crm,
        rgbaColor(getColor("danger"), 0.3),
        "dashed",
        getColor("danger"),
        checkboxes?.crm
      ),
      // getSeries(
      //   "App",
      //   totalTodaySums?.app,
      //   rgbaColor(getColor("info"), 0.3),
      //   "solid",
      //   getColor("info"),
      //   checkboxes?.app
      // ),
      // getSeries(
      //   "prev App",
      //   totalYesterdaySums?.app,
      //   rgbaColor(getColor("info"), 0.3),
      //   "dashed",
      //   getColor("info"),
      //   checkboxes?.app
      // ),
      getSeries(
        "App Shopflo",
        totalTodaySums?.app_shopflo,
        rgbaColor(getColor("info"), 0.3),
        "solid",
        getColor("info"),
        checkboxes?.app_shopflo
      ),
      getSeries(
        "prev App Shopflo",
        totalYesterdaySums?.app_shopflo,
        rgbaColor(getColor("info"), 0.3),
        "dashed",
        getColor("info"),
        checkboxes?.app_shopflo
      ),
      getSeries(
        "App Juspay",
        totalTodaySums?.app_juspay,
        rgbaColor(getColor("success"), 0.3),
        "solid",
        getColor("success"),
        checkboxes?.app_juspay
      ),
      getSeries(
        "prev App Juspay",
        totalYesterdaySums?.app_juspay,
        rgbaColor(getColor("success"), 0.3),
        "dashed",
        getColor("success"),
        checkboxes?.app_juspay
      ),
    ];
    const filteredArray = series.filter((value) => value !== null);
    return {
      color: getColor("gray-100"),
      tooltip: {
        trigger: "axis",
        padding: [7, 10],
        backgroundColor: getColor("gray-100"),
        borderColor: getColor("gray-100"),
        textStyle: { color: getColor("black") },
        borderWidth: 1,
        formatter: tooltipFormatter,
        transitionDuration: 0,
      },
      legend: {
        data: days,
        show: false,
      },
      xAxis: {
        type: "category",
        data: days,
        boundaryGap: false,
        axisPointer: {
          lineStyle: {
            color: getColor("gray-300"),
            type: "dashed",
          },
        },
        splitLine: { show: false },
        axisLine: {
          lineStyle: {
            color: rgbaColor("#000", 0.01),
            type: "dashed",
          },
        },
        axisTick: { show: false },
        axisLabel: {
          color: getColor("gray-600"),
          margin: 15,
        },
      },
      yAxis: {
        type: "value",
        axisPointer: { show: false },
        splitLine: {
          lineStyle: {
            color: getColor("gray-300"),
            type: "dashed",
          },
        },
        boundaryGap: false,
        axisLabel: {
          show: true,
          color: getColor("gray-600"),
          margin: 15,
        },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      series: filteredArray,
      grid: { right: "18px", left: "67px", bottom: "7%", top: "5%" },
    };
  };

  return (
    <ReactEChartsCore
      key={JSON.stringify(getOption())}
      echarts={echarts}
      option={getOption()}
      style={{ height: "50vh" }}
    />
  );
}

export default FinancialGraph;
